import { createContext, ReactNode, use, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DOWN as SwipeDirectionDown, LEFT as SwipeDirectionLeft, RIGHT as SwipeDirectionRight } from 'react-swipeable';
import styled from 'styled-components';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useHousehold } from '../data/use_household.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { songSlugSearchParamName } from '../util/shared.tsx';
import { CurrentSongContext } from './current_song.tsx';
import { useMixerVisibility } from './use_mixer_visibility.ts';

export const ThumbSize = '48px';
export const SliderButtonAndRangeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: calc(${ThumbSize} + 12px);

  @media (orientation: landscape) {
    height: calc(${ThumbSize} + 10px);
  }
`;
export const sliderButtonWidth = '10ch';

export const SliderButtonWrapper = styled.div`
  flex-shrink: 0;
  width: ${sliderButtonWidth};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
`;

export const SliderBase = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  width: 100%;
  height: fit-content;

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 8.4px;
    cursor: pointer;
    background: var(--color-slider);
    border: none;
    /*box-shadow: 1px 1px 1px #000000, 0 0 1px #0d0d0d;*/
    /*border-radius: 1.3px;*/
    /*border: 0.2px solid #010101;*/
  }
`;
export const SliderLabel = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: larger;
  cursor: inherit;
`;
export const VoiceButtonWrapper = styled.div<{ $narrow?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.$narrow ? 'fit-content' : '100%')};
  box-shadow: 1.3px 1.3px 3px 0 #b0b0b0;
  border-radius: 6px;
  position: relative;
  cursor: pointer;
  &:active {
    transform: translateY(2px) translateX(2px);
  }
`;
export const locationSliderUpdateIntervalMs = 100;

// min px values for swiping
export const swipeDeltas = {
  [SwipeDirectionLeft.toLowerCase()]: 100,
  [SwipeDirectionRight.toLowerCase()]: 100,
  [SwipeDirectionDown.toLowerCase()]: 150,
};

export const swipeDurationMs = 1000;

export interface PlayerContextProps {
  onMinimize: (event?: any) => void;

  isReportSongIssueVisible: boolean;
  onShowReportSongIssue: () => void;
  onHideReportSongIssue: () => void;

  areSongSettingsVisible: boolean;
  onShowSongSettingsMenu: (event: any) => void;
  onHideSongSettingsMenu: () => void;
}

export const PlayerContext = createContext<PlayerContextProps>({} as PlayerContextProps);
export const PlayerContextProvider = ({
  children,
  initialAreSongSettingsVisible = false,
}: {
  children: ReactNode;
  initialAreSongSettingsVisible?: boolean;
}) => {
  const [_, setIsPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const [areSongSettingsVisible, setAreSongSettingsVisible] = useState(initialAreSongSettingsVisible);
  const [isReportSongIssueVisible, setIsReportSongIssueVisible] = useState(false);
  const { household } = useHousehold();
  const navigate = useNavigate();
  const { tutorialPlayingSong } = use(CurrentSongContext);

  const onShowReportSongIssue = useCallback(() => {
    setIsReportSongIssueVisible(true);
  }, []);

  const onHideReportSongIssue = useCallback(() => {
    setIsReportSongIssueVisible(false);
  }, []);

  const onShowSongSettingsMenu = useCallback(
    (event: any) => {
      if (tutorialPlayingSong) {
        return;
      }
      setAreSongSettingsVisible(true);
      event.stopPropagation(); // prevent mixer from closing
    },
    [tutorialPlayingSong],
  );

  const onHideSongSettingsMenu = useCallback(() => {
    setAreSongSettingsVisible(false);
  }, []);

  const onMinimize = useCallback(
    (event?: any) => {
      if (tutorialPlayingSong) {
        return;
      }
      event?.stopPropagation(); // prevent mixer from closing
      onHideSongSettingsMenu();
      onHideReportSongIssue();
      setIsPlayerMaximized(false);
      if (!household) {
        void navigate('/');
        return;
      }
      // Only take the song out of the URL. Rebuilding the URL from the page name dropped any
      // sub-path, and this runs behind the router's back: the Family Worship tutorial closes
      // the player on start, which rewrote /tutorials/family-worship to /tutorials, so Back from
      // the tutorials list returned to that same list and left a blank page (GitHub issue #3551).
      const url = new URL(window.location.href);
      if (!url.searchParams.has(songSlugSearchParamName)) {
        return;
      }
      url.searchParams.delete(songSlugSearchParamName);
      history.replaceState(history.state, document.title, url.pathname + url.search + url.hash);
    },
    [household, navigate, onHideReportSongIssue, onHideSongSettingsMenu, setIsPlayerMaximized, tutorialPlayingSong],
  );

  return (
    <PlayerContext
      value={{
        onMinimize,
        isReportSongIssueVisible,
        onShowReportSongIssue,
        onHideReportSongIssue,
        areSongSettingsVisible,
        onShowSongSettingsMenu,
        onHideSongSettingsMenu,
      }}
    >
      <PlayerCssVariables>{children}</PlayerCssVariables>
    </PlayerContext>
  );
};

// display: contents — this element exists only to scope --play-button-size
// to its descendants, not to participate in layout. Without this, Storybook
// wrappers see PlayerCssVariables shrink to its content and Player's
// height: 100% no longer fills the test wrapper.
const PlayerCssVariables = styled.div`
  display: contents;
  --play-button-size: max(min(max(8vh, 8vw), 85px), 70px);
`;

const opacityWhilePlaying = '0.2';
const buttonFadeTime = '1000ms';

export function useButtonFade() {
  const { isMixerVisible } = useMixerVisibility();
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  return !isMixerVisible && isPlayerMaximized;
}

export const buttonShadowSize = '6px';
export const buttonDropShadow = `2px 2px ${buttonShadowSize} rgb(from var(--color-text) r g b / var(--opacity-button-drop-shadow))`;
export const FadingButton = styled.button<{ $fade?: boolean; $noShadow?: boolean }>`
  transition:
    opacity ${buttonFadeTime} ease,
    box-shadow ${buttonFadeTime} ease,
    background ${buttonFadeTime} ease,
    fill ${buttonFadeTime} ease;

  border: none;
  opacity: ${(props) => (props.$fade ? opacityWhilePlaying : 1)};
  box-shadow: ${(props) => (props.$fade || props.$noShadow ? 'none' : buttonDropShadow)};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      opacity: 1;
      ${(props) => (!props.$noShadow ? `box-shadow: ${buttonDropShadow};` : '')}
    }
  }
`;
